import { ComponentItem } from "designSystem/types/component-item-types";
import DojoCreateWidget from "csi/dojoInReact/DojoCreateWidget";
import { EMPTY_URL } from "csi/constants";
import ArrayDataStore from "csi/datastore/ArrayDataStore";
import enums from "csi/enums";
import locale from "csi/locale";
import reflection from "csi/reflection";
import types from "csi/types";
import DateRange from "csi/widget/DateRange";
import xml from "csi/xml";
import Query from "csi/xml/Query";
import Search from "csi/xml/Search";
import storage from "csi/_base/storage";
import React, { FunctionComponent } from "react";
import { FilterParams } from "./component/MultiFilter";

const FILTER_ITEM_ATTRIBUTES = 0;
const FILTER_ITEM_C8OBJECT = 1;
const FILTER_ITEM_CHANGETIME = 2;
const FILTER_ITEM_USERS = 3;
const FILTER_ITEM_OPERATION = 4;

const TYPE_DOMAIN_CLASS = "DomainClass";
const TYPE_CHANGE_TIME = "ChangeTime";
const TYPE_OPERATION = "Operation";
const TYPE_LOG_CURRENT_USER = "LogCurrentUser";
const TYPE_ATTRUBITES = "Node Name:Child:Attributes";
const TYPE_CHNAGE_LOG_ENTRY = "ChangeLogEntry";

const OPERATION_VALUES_BY_XML = "ValuesByXML";
const MODULE_SEARCH = "Search";

// create object for main data structure
function getResObject(
	id: string,
	type: string,
	res: string,
	store: ArrayDataStore<ComponentItem> = null,
	queryFilter?,
	disableMultiSelect = () => false,
	filterControl: object = null,
	selection: ComponentItem[] = [],
) {
	return {
		id: id,
		name: type ? locale.typeId(type, res) : locale.resource(res),
		type: type,
		store,
		queryFilter,
		disableMultiSelect,
		filterControl,
		selection,
	};
}

type SlimViwDateRange = {
	/** id identify chosen type of date range (Today, Last 24 hours, Last Month etc) */
	id: number;
	/** localized name of selected date range */
	name: string;
	/** date range range start */
	timeFrom: number;
	/** date range range end */
	timeTo: number;
}

/**
 * convert slim filter value to standard dateRange value
 */
function toDateRange(val: SlimViwDateRange): number[] {
	return [val.timeFrom, val.timeTo, val.id];
}

export function getFilterData(locationType: string): FilterParams {
	const getData = (filterId: number) => filterParams.filterData[filterId];

	const getDateRangeName = (sel: number[]): string => {
		const TimeSelection = types.DateRangeSelection;
		const getDateRangeRes = types.getDateRangeRes;

		// for filter by time, we save 3 values: from, to, index of selection (0-4)
		switch (sel[2]) {
			case TimeSelection.CUSTOM:
				return types.getFormattedDateString(new Date(sel[0])) + " - " + types.getFormattedDateString(new Date(sel[1]));
			default:
				return locale.resource(getDateRangeRes(sel[2]));
		}
	};

	const getDateRangeComponent = (onChangeSelection: (event, newValue, reason) => void) => {
		const data = getData(FILTER_ITEM_CHANGETIME);
		const dataSelection = data.selection.length ? data.selection[0] : null;
		// convert date range to dojo component format
		const value = dataSelection ? [dataSelection.timeFrom, dataSelection.timeTo, dataSelection.id] : null;

		const onChangeSelectionHelper = (newValue) => {
			onChangeSelection({}, newValue, false);
		};

		return (
			<DojoCreateWidget
				widgetClass={DateRange}
				widgetClassParams={{
					popupParams: {
						allowBlanks: false,
						allowTime: true,
						inPast: true,
					},
					onChange: onChangeSelectionHelper,
					value: value,
				}}
			/>
		);
	};

	// C8 OBJECT values store
	const multiselectStoreC8Object = new ArrayDataStore<ComponentItem>(null, "name",
		async (): Promise<ComponentItem[]> => {
			const search = new Search({
				Module: "ChangeTracking",
				Operation: "GetEnabledChildConfigurations",
				DomainClass: locationType,
				Depth: 4,
			});

			const response = await search.execute();
			return response.getReturnValue().map((item) => {
				return {
					id: item,
					name: locale.type(item),
				};
			});
		},
	);

	// C8 OBJECT values search query
	const C8ObjectQueryFilter = (query: Query) => {
		const data = getData(FILTER_ITEM_C8OBJECT);
		const selection = data.selection;
		const len = selection.length;
		let q = query;
		if (len > 0) {
			if (len > 1) {
				q = q.Or();
			}
			selection.forEach((item) => {
				q.Attr(TYPE_DOMAIN_CLASS, xml.OP_EQUALS, item.id);
			});
		}
	};

	// OPERATIONS values store
	const multiselectStoreOperations = new ArrayDataStore<ComponentItem>(null, "name",
		async (): Promise<ComponentItem[]> => {
			const enumStore = enums.getStore("ChangeTrackingType");
			// filter out empty value
			return enumStore._store.data.filter((item) => { return item.name; });
		},
	);

	// OPERATIONS values search query
	const OperationQueryFilter = (query: Query) => {
		const data = getData(FILTER_ITEM_OPERATION);
		const selection = data.selection;
		const len = selection.length;
		let q = query;
		if (len > 0) {
			if (len > 1) {
				q = q.Or();
			}
			selection.forEach((item) => {
				q.Attr(TYPE_OPERATION, xml.OP_EQUALS, item.id);
			});
		}
	};

	// USERS values store
	const multiselectStoreUsers = new ArrayDataStore<ComponentItem>(null, "name",
		async (): Promise<ComponentItem[]> => {
			const search = new Search({
				Module: MODULE_SEARCH,
				Operation: OPERATION_VALUES_BY_XML,
				QRY_VALUE_ATTR: xml.ATTR_NAME,
			});
			search.getQuery()
				.Type(xml.TYPE_USER)
				.Or()
				.Attr("UserType", xml.OP_NOT_EQUALS, "UserType:CentricService")
				.Node(xml.PROP_URL, xml.OP_EQUALS, "centric://REFLECTION/INSTANCE/User/System");

			const response = await search.execute();

			return response.getValues().map((item) => {
				return {
					id: item.Value,
					name: item.Value,
				};
			});
		},
	);

	// USERS values search query
	const UsersQueryFilter = (query: Query) => {
		const data = getData(FILTER_ITEM_USERS);
		const selection = data.selection;
		const len = selection.length;
		let q = query;
		if (len > 0) {
			if (len > 1) {
				q = q.Or();
			}
			selection.forEach((item) => {
				q.Attr("CurrentUser", xml.OP_EQUALS, item.name);
			});
		}
	};

	// ATTRIBUTES values store
	const multiselectStoreAttributes = new ArrayDataStore<ComponentItem>(null, "name",
		async (): Promise<ComponentItem[]> => {
			const c8Type = getData(FILTER_ITEM_C8OBJECT).selection[0].id;
			const dc = reflection.getDomainClass(c8Type);
			if (dc) {
				var rc = reflection.getClass("DomainAttributeConfiguration");
				const search = new Search({
					Module: MODULE_SEARCH,
					Operation: OPERATION_VALUES_BY_XML,
					QRY_VALUE_ATTR: xml.ATTR_NAME,
				});
				search.getQuery()
					.Type(rc.getType())
					.Attr("ChangeTrackingEnabled", xml.OP_EQUALS, true)
					.Attr(xml.ATTR_NAME, xml.OP_EQUALS, dc.type, reflection.PATH_PARENT);

				const response = await search.execute();

				return response.getValues()
					.map((item) => {
						return {
							id: item.Value,
							name: locale.typeId(dc.getDomainAttribute(item.Value).rca.getOwner().type, item.Value),
						};
					})
					.sort((a, b) => {
						return a.name.localeCompare(b.name);
					});
			}
		},
	);

	// ATTRIBUTES values search query
	const AttributesQueryFilter = (query: Query) => {
		const data = getData(FILTER_ITEM_ATTRIBUTES);
		const selection = data.selection;
		const len = selection.length;
		let q = query;
		if (len > 0) {
			if (len > 1) {
				q = q.Or();
			}
			selection.forEach((item) => {
				q.Attr("Node Name", xml.OP_EQUALS, item.id, "(ChangeLogEntry)Child:Attributes(ChangeLogAttributeEntry)");
			});
		}
	};

	// ATTRIBUTES values select disable conditions
	const disableSelectAttribute = () => getData(FILTER_ITEM_C8OBJECT).selection.length != 1;

	// CHANGE TIME values store
	const multiselectStoreChangeTime = new ArrayDataStore<ComponentItem>([], "name", null);

	// CHANGE TIME values search query
	const ChangeTimeQueryFilter = (query: Query) => {
		const data = getData(FILTER_ITEM_CHANGETIME);
		const selection = data.selection;
		const len = selection.length;
		const q = query;

		if ((len > 0) && selection[0].timeFrom && selection[0].timeTo) {
			// get date range in absolute format
			const absValue = types.updateDateRange(toDateRange(selection[0]));

			q.Attr(TYPE_CHANGE_TIME, xml.OP_GREATER_EQUAL, absValue[0]);
			q.Attr(TYPE_CHANGE_TIME, xml.OP_LESS_EQUAL, absValue[1]);
		}
	};

	// values (second select) onChange
	const onChangeSelection = (selectedFilter?: number, newValue?: any) => {
		// if C8Object selection changed - reset Attribute selection;
		const attributesData = getData(FILTER_ITEM_ATTRIBUTES);
		switch (selectedFilter) {
			case FILTER_ITEM_C8OBJECT:
				attributesData.selection = [];
				// reset attribute store for new C8Object
				if (newValue?.length == 1) {
					delete attributesData.store;
					attributesData.store = multiselectStoreAttributes;
				};
				break;
			case FILTER_ITEM_CHANGETIME:
				// check if user made no selection
				if (!types.isDateRangeValue(newValue)) {
					return;
				}
				const data = getData(FILTER_ITEM_CHANGETIME);
				// convert date range from dojo component format
				data.selection = [{
					id: newValue[2],
					name: getDateRangeName(newValue),
					timeFrom: newValue[0],
					timeTo: newValue[1],
				}];
				break;
		}

		saveSelection();
	};

	// if delete C8Objects chip - make sure to remove attributes selection
	const onChipDelete = (item: ComponentItem) => {
		if (item.id == TYPE_DOMAIN_CLASS) {
			getData(FILTER_ITEM_ATTRIBUTES).selection = [];
		}
		saveSelection();
	};

	// define main data structure
	const filterParams = {
		filterData: [
			getResObject(TYPE_ATTRUBITES, null, "ReflectionAttributeId", multiselectStoreAttributes, AttributesQueryFilter, disableSelectAttribute),
			getResObject(TYPE_DOMAIN_CLASS, TYPE_CHNAGE_LOG_ENTRY, TYPE_DOMAIN_CLASS, multiselectStoreC8Object, C8ObjectQueryFilter),
			getResObject(TYPE_CHANGE_TIME, TYPE_CHNAGE_LOG_ENTRY, TYPE_CHANGE_TIME, multiselectStoreChangeTime, ChangeTimeQueryFilter, () => false, getDateRangeComponent),
			getResObject(TYPE_LOG_CURRENT_USER, null, TYPE_LOG_CURRENT_USER, multiselectStoreUsers, UsersQueryFilter),
			getResObject(TYPE_OPERATION, TYPE_CHNAGE_LOG_ENTRY, TYPE_OPERATION, multiselectStoreOperations, OperationQueryFilter),
		],
		callbackOnChangeSelection: onChangeSelection,
		callbackOnChipDelete: onChipDelete,
		chipItems: {},
		selectedFilterItem: FILTER_ITEM_C8OBJECT,
	};

	// load selection from local storage
	// Change Log "Slim View" and "Detailed View" share the same fitler save, so filter change made in one view appear in other view
	const loadSelection = () => {
		const defSelection = storage.get("ChangeLog|_CS_Site|" + locationType)?.query || [];
		filterParams.filterData.forEach((filterItem, idx) => {
			const sel = defSelection[filterItem.id] || [];
			if (sel.length) {
				let itemName;
				if (idx == FILTER_ITEM_CHANGETIME) {
					itemName = getDateRangeName(sel);
					filterItem.selection.push({
						id: sel[2], // type of selection (Today, Past Week, Past Month....)
						name: itemName, // localized name of given selection type
						timeFrom: sel[0],
						timeTo: sel[1],
					});
				} else {
					sel.forEach((selItem, selNum) => {
						switch (idx) {
							case FILTER_ITEM_ATTRIBUTES:
								// check for broken save: not possible to have selected attributes without single c8 class selection
								if (defSelection[TYPE_DOMAIN_CLASS] || defSelection[TYPE_DOMAIN_CLASS] == 1) {
									// to localize C8 object attribute we need to use object type
									itemName = locale.typeId(defSelection[TYPE_DOMAIN_CLASS][0], selItem);
								}
								break;
							case FILTER_ITEM_OPERATION:
								itemName = locale.getEnumName(selItem, false);
								break;
							default:
								itemName = locale.resource(selItem);
						}
						if (selItem && itemName) {
							filterItem.selection.push({
								id: selItem,
								name: itemName,
							});
						}
					});
				}
			}
		});
	};

	// save aelection to local storage
	// Change Log "Slim View" and "Detailed View" share the same fitler save, so filter change made in one view appear in other view
	const saveSelection = () => {
		const storageKey = "ChangeLog|_CS_Site|" + locationType;
		const saveData = storage.get(storageKey) || {};

		// if no save data yet - simulate Default CV for compatibility with Cahnge Log Detailed View
		saveData.CV ||= EMPTY_URL;

		const defSelection = {};
		filterParams.filterData.forEach((filterItem, idx) => {
			const selection = filterItem.selection;
			if (selection.length) {
				defSelection[filterItem.id] = [];
				if (idx == FILTER_ITEM_CHANGETIME) {
					const dateRangeSelection = selection[0];
					defSelection[filterItem.id].push(dateRangeSelection.timeFrom, dateRangeSelection.timeTo, dateRangeSelection.id);
				} else {
					selection.forEach((selectionItem) => {
						defSelection[filterItem.id].push(selectionItem.id);
					});
				}
			};
		});
		saveData.query = saveData.queryForServer = defSelection;
		storage.set(storageKey, saveData);
	};

	loadSelection();

	return filterParams;
}

export default getFilterData;